/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { RootState } from 'store';

import { flightResults, getAirOptionIdFromGroupedResults } from 'actions/flights';
import CardGroupedFlights from 'components/Cards/DataCardGroupedFlights';
import FilterTag from 'components/Filters/components/FilterTag';
import HeaderSearchDesktop from 'components/HeaderSearchDesktop';
import { Desktop, Mobile } from 'components/Responsive';
import TopButton from 'components/TopButton';
import FlightsFilters from 'containers/FlightsFilters';
import FlightsSearchEdit from 'containers/FlightsSearchEdit';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { FlightFilter, FlightFilterResponse } from 'models/flightFilter';
import { FlightResultRequestOrderCriteriaEnum, FlightGroupedResultResponse, FlightStretch } from 'models/flightSearch';
import { FlightTabFilter } from 'models/flightTabFilter';
import { MultidestinationTrip } from 'models/multiDestinationTrip';
import FlightDetails from 'screens/containers/FlightDetails';
import UpSellOptions from 'screens/containers/UpsellOptions';
import {
  amplitudelogEvent,
  FilterGroupEvent,
  SearchEvent,
  SelectFilterEvent,
  SelectViewFiltersEvent,
  SelectViewUpsellOptionsEvent,
} from 'services/amplitudeService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { getFormattedPrice } from 'utils/currency';
import { daysCount, formatNumberToCompact, getAirlineForInitials, htmlT } from 'utils/generic';

import DesktopSearchBar from '../DesktopSearchBar';
import BestOptions from './components/BestOptions';
import FlightsCompare from './components/FlightsCompare';
import FloatingFilter from './components/FloatingFilter';
import Header from './components/Header';
import CardLoader from './components/LoadingCard';
import LoadingFilter from './components/LoadingFilter';
import LoadingFlightsCompare from './components/LoadingFlightsCompare';
import {
  Container,
  Body,
  Cards,
  NotFound,
  FilterTagsContainer,
  RoundTripIcon,
  OneWayIcon,
  Wrapper,
  LeftContainer,
} from './styles';
import Tooltip from 'components/Tooltips/Tooltip';

interface Props {
  departurePoint?: Airport;
  arrivalPoint?: Airport;
  departureDate?: Date;
  returnDate?: Date;
  isLoading: boolean;
  isLoadingResults: boolean;
  flightsResult: FlightGroupedResultResponse[];
  orderCriteria: FlightResultRequestOrderCriteriaEnum;
  flightTabFilter?: FlightTabFilter[];
  currency: string;
  onRequest: (orderCriteria?: FlightResultRequestOrderCriteriaEnum) => void;
  availableFilters?: FlightFilterResponse;
  roundTrip: boolean;
  baggageIncluded: boolean;
  directFlight: boolean;
  cabin?: string;
  travelers?: TravelArrangerUsersAllowed[];
  multiDestinationTrips?: MultidestinationTrip[];
  flightStretch?: FlightStretch;
}

const formatDuration = (duration: string) => {
  if (duration.length === 2) return `${duration.replace(/^0/, '')}h`;
  return `${duration.substring(0, 5).replace(':', 'h').replace(/^0/, '')}m`.replace('00m', '');
};

const FlightsList = ({
  flightsResult,
  isLoading,
  isLoadingResults,
  departurePoint,
  arrivalPoint,
  departureDate,
  returnDate,
  orderCriteria,
  onRequest,
  flightTabFilter,
  currency,
  availableFilters,
  roundTrip,
  baggageIncluded,
  directFlight,
  cabin,
  travelers,
  multiDestinationTrips,
  flightStretch,
}: Props) => {
  const isDesktop = useIsDesktop();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency, t });
    return formatted;
  };
  const filters = useSelector((state: RootState) => state.flights.filters);
  const user = useSelector((state: RootState) => state.user.accountResult);
  const distanceRestriction = useSelector((state: RootState) => state.flights.search?.distanceRestriction);
  const [showDetails, setShowDetails] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showUpSell, setShowUpSell] = useState(false);
  const [directFlights, setDirectFlights] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState<FlightGroupedResultResponse | undefined>();
  const [flightOptionId, setFlightOptionId] = useState<number | undefined>();
  const [filterSearching, setFilterSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [upSellId, setUpSellId] = useState<string>();
  const [upSellOptionId, setUpSellOptionId] = useState<number>(0);
  const searchId = useSelector((state: RootState) => state.flights.search?.searchId);

  const amplitudeBaseEvent: SearchEvent = {
    TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
    TRAVEL_FROM: departurePoint?.code ?? '',
    TRAVEL_TO: arrivalPoint?.code ?? '',
    TRAVEL_DESTINATION_TYPE: '',
    TRAVEL_TRIP_TYPE: roundTrip ? 'true' : 'false',
    TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
    TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
    TRAVEL_DATE_TO: returnDate?.toString() ?? '',
    TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
    TRAVEL_PASSENGERS: '1',
    TRAVEL_ADULTS: '1',
    TRAVEL_CHILDREN: '0',
    TRAVEL_INFANTS: '0',
    TRAVEL_CLASS_OF_SERVICE: cabin ?? '',
    TRAVEL_ROOMS: '',
    TRAVEL_WITH_BAGGAGE: baggageIncluded ? 'true' : 'false',
    TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
    SEARCHID: searchId ?? '',
  };

  const amplitudeViewFiltersEvent: SelectViewFiltersEvent = {
    ...amplitudeBaseEvent,
    VIEW_TYPE: '',
  };

  const amplitudeSelectFilter: SelectFilterEvent = {
    ...amplitudeBaseEvent,
    FILTER_GROUP: '',
    FILTER_VALUE: '',
  };

  const amplitudeSelectUpsell: SelectViewUpsellOptionsEvent = {
    ...amplitudeBaseEvent,
    TRAVEL_PRODUCT_NAME: '',
    TRAVEL_ADVANCED_PURCHASE: 'FLEX',
    SOURCE: 'RESULTS',
  };

  const handleScroll = useCallback(
    (e: Event) => {
      if (e.target !== document) return;
      if (isLoading || isLoadingResults || filterSearching) return;
      const hasReachedBottom = document.body.scrollHeight - window.scrollY <= document.body.clientHeight + 700;
      if (hasReachedBottom) onRequest();
    },
    [filterSearching, isLoading, isLoadingResults, onRequest],
  );

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    history.listen((location) => {
      if (location.pathname === '/corporate/flights') {
        setShowDetails(false);
        setShowFilters(false);
        setShowEdit(false);
        setShowUpSell(false);
      }
      if (location.pathname === '/corporate/flights/details') {
        setShowDetails(true);
        setShowFilters(false);
        setShowEdit(false);
        setShowUpSell(false);
      }
      if (location.pathname === '/corporate/flights/filters') {
        setShowDetails(false);
        setShowFilters(true);
        setShowEdit(false);
        setShowUpSell(false);
      }
      if (location.pathname === '/corporate/flights/upsell') {
        setShowDetails(false);
        setShowFilters(false);
        setShowEdit(false);
        setShowUpSell(true);
      }
      if (location.pathname === '/corporate/flights/edit') {
        setShowEdit(true);
      }
    });
  }, [history]);

  useEffect(() => {
    setDirectFlights(Boolean(filters?.stops?.find((item) => item === '0')));
  }, [filters]);

  const scrollTop = () => window.scrollTo({ top: 0 });

  const getTabFilterAmount = (key: FlightResultRequestOrderCriteriaEnum) => {
    if (flightTabFilter) {
      const tabFilter = flightTabFilter.find((filterTab: FlightTabFilter) => filterTab.orderCriteria === key);
      const tabValue = tabFilter ? tabFilter.amount : 0;
      return formattedPrice(tabValue);
    }
    return null;
  };

  const getTabFilterDuration = (key: FlightResultRequestOrderCriteriaEnum) => {
    if (flightTabFilter) {
      const tabFilter = flightTabFilter.find((filterTab: FlightTabFilter) => filterTab.orderCriteria === key);
      const tabValue = tabFilter ? tabFilter.duration : '00:00:00';
      const days = tabValue.split('.').length === 1 ? 0 : Number(tabValue.split('.')[0]);
      const tabTime = tabValue.split('.').last();
      const [hours, minutes] = tabTime.split(':').map(Number);
      return moment
        .duration(days * 24 * 60 + hours * 60 + minutes, 'minutes')
        .format('d[d] h[h] m[m]', { trim: 'all' });
    }
    return null;
  };

  const handleClickCard = useCallback(
    async (flight: FlightGroupedResultResponse, selectedFlightKeys: []) => {
      console.log('handleClickCard');
      setFlightOptionId(undefined);
      history.push('/corporate/flights/details');

      const { optionId } = await dispatch<any>(getAirOptionIdFromGroupedResults(selectedFlightKeys));
      setFlightOptionId(optionId);

      setSelectedFlight(flight);
      setUpSellOptionId(0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history],
  );

  const handleClickBack = () => {
    history.goBack();
  };

  const handleFilterClick = () => {
    amplitudeViewFiltersEvent.VIEW_TYPE = '';
    amplitudelogEvent(amplitudeEvents.SELECT_VIEW_FILTERS, amplitudeViewFiltersEvent, user);

    history.push('/corporate/flights/filters');
  };

  const handleFilterChange = async (filters: FlightFilter) => {
    setFilterSearching(true);
    setTimeout(scrollTop);
    await dispatch(flightResults(orderCriteria, filters));
    setFilterSearching(false);
  };
  const handleCompareChange = async (filters: FlightFilter) => {
    setFilterSearching(true);
    setTimeout(scrollTop);
    await dispatch(flightResults(FlightResultRequestOrderCriteriaEnum.PRICE, filters));
    setFilterSearching(false);
  };

  const handleFilterDirectFlights = async () => {
    amplitudeSelectFilter.FILTER_GROUP = FilterGroupEvent.STOPS;
    amplitudeSelectFilter.FILTER_VALUE = !directFlights ? 'DIRECT' : 'AV';

    amplitudelogEvent(amplitudeEvents.SELECT_FILTER, amplitudeSelectFilter, user);

    setDirectFlights(!directFlights);
    scrollTop();

    setFilterSearching(true);

    if (filters) {
      const newFilters: FlightFilter = { ...filters };
      if (directFlights) {
        newFilters.stops = filters.stops?.filter((item) => item !== '0');
      } else newFilters.stops = ['0'];

      await dispatch(flightResults(orderCriteria, newFilters));
    } else {
      await dispatch(
        flightResults(
          orderCriteria,
          !directFlights ? ({ stops: ['0'] } as FlightFilter) : ({ stops: [] } as FlightFilter),
        ),
      );
    }
    setFilterSearching(false);
  };

  const handleEditClick = () => {
    setShowEdit(true);
    history.push('/corporate/flights/edit');
  };

  const handleUpSellClick = async (flight: FlightGroupedResultResponse, idx: number, selectedFlightKeys: []) => {
    setSelectedFlight(flight);
    setSelectedIndex(idx);
    setShowUpSell(true);
    history.push('/corporate/flights/upsell');

    setFlightOptionId(undefined);
    const { optionId } = await dispatch<any>(getAirOptionIdFromGroupedResults(selectedFlightKeys));
    setFlightOptionId(optionId);
  };

  const handleUpSellChange = (optionId: number) => {
    amplitudeSelectUpsell.TRAVEL_PRODUCT_NAME = selectedFlight?.provider ?? '';
    if (optionId > 1) amplitudelogEvent(amplitudeEvents.SELECT_UPSELL_OPTIONS, amplitudeSelectUpsell, user);
  };

  const handleUpSellSelect = (optionId: number, upSellId: string) => {
    setUpSellOptionId(optionId);
    setUpSellId(upSellId);
    history.replace('/corporate/flights/details');
  };

  const renderLoadingContent = () => (
    <Cards>
      <Desktop>
        <LoadingFlightsCompare />
      </Desktop>
      <LoadingFilter qty={3} hasFilters={hasFilters} />
      {Number(3)
        .toArray()
        .map((number) => (
          <CardLoader
            key={number}
            col={{
              sm: 6,
              md: 6,
              lg: 6,
              xl: 6,
            }}
            roundTrip={!!returnDate}
          />
        ))}
    </Cards>
  );

  const renderCards = () => {
    if (!isLoading && !isLoadingResults && !filterSearching && flightsResult.length === 0) {
      return (
        <NotFound>
          <NotFound.Icon />
          {t('flightsList.notFound')}
        </NotFound>
      );
    }

    return (
      <Cards>
        {!isLoading &&
          !filterSearching &&
          flightsResult.map((flight, index) => (
            <CardGroupedFlights
              key={`flight-${index}`}
              data={flight}
              onClickWithUpSell={async (selectedFlightKeys) => {
                handleUpSellClick(flight, index, selectedFlightKeys);
              }}
              onClickWithoutUpSell={async (selectedFlightKeys) => {
                handleClickCard(flight, selectedFlightKeys);
              }}
              testId={index === 0 ? 'flight-list-first-item' : undefined}
              departurePoint={{...departurePoint, airport_code: flight.sectors.first().options.first().flights.first().arrivalAirport}}
              arrivalPoint={{...arrivalPoint, airport_code: flight.sectors.last().options.last().flights.last().arrivalAirport}}
              departureDate={departureDate}
              returnDate={returnDate}
              numberOfTravelers={travelers.length}
              flightStretch={flightStretch}
            />
          ))}
        {(isLoading || isLoadingResults || filterSearching) &&
          Number(3)
            .toArray()
            .map((number) => (
              <CardLoader
                key={number}
                col={{
                  sm: 6,
                  md: 6,
                  lg: 6,
                  xl: 6,
                }}
                roundTrip={roundTrip}
              />
            ))}
      </Cards>
    );
  };

  const renderFilterTags = () => {
    if (!filters) return null;

    return (
      <>
        {filters.priceFrom && filters.priceTo && (
          <FilterTag
            onDelete={() => {
              const newFilters: FlightFilter = {
                ...filters,
                priceFrom: undefined,
                priceTo: undefined,
              };
              handleFilterChange(newFilters);
            }}
          >
            {t('flightsList.filterTags.priceRange', {
              from: formatNumberToCompact(t, filters.priceFrom),
              to: formatNumberToCompact(t, filters.priceTo),
            })}
          </FilterTag>
        )}

        {filters.airlines?.map((airline) => (
          <FilterTag
            key={airline}
            onDelete={() => {
              const newFilters: FlightFilter = {
                ...filters,
                airlines: filters.airlines?.filter((a) => a !== airline),
              };
              handleFilterChange(newFilters);
            }}
          >
            {airline === 'MultipleCarriers' ? t('flightsFilters.airlines.multiple') : getAirlineForInitials(airline)}
          </FilterTag>
        ))}

        {filters.stops?.map((stop) => (
          <FilterTag
            key={stop}
            onDelete={() => {
              const newFilters: FlightFilter = {
                ...filters,
                stops: filters.stops?.filter((s) => s !== stop),
              };
              handleFilterChange(newFilters);
            }}
          >
            {stop === '0' ? t('flightsList.direct') : t('flightsList.filterTags.stop', { count: Number(stop) })}
          </FilterTag>
        ))}

        {filters.travelPolicies?.map((travelPolicy) => (
          
          <FilterTag
            key={travelPolicy}
            onDelete={() => {
              const newFilters: FlightFilter = {
                ...filters,
                travelPolicies: filters.travelPolicies?.filter((s) => s !== travelPolicy),
              };
              handleFilterChange(newFilters);
            }}
          >
            {travelPolicy === 'outOfPolicies' ? t('flightsList.filterTags.outOfPolicies') : t('flightsList.filterTags.withInPolicies')}

          </FilterTag>
        ))}

        {filters.departureTimes?.map((departureTime) => (
          <FilterTag
            key={departureTime.sectorNumber}
            onDelete={() => {
              const newFilters: FlightFilter = {
                ...filters,
                departureTimes: filters.departureTimes?.filter((dt) => dt.sectorNumber !== departureTime.sectorNumber),
              };
              handleFilterChange(newFilters);
            }}
          >
            {t('flightsList.filterTags.departureTimeRange', {
              departure: departureTime.text?.split(' - ')[0] || '',
              destination: departureTime.text?.split(' - ')[1] || '',
              from: formatDuration(departureTime.from),
              to: formatDuration(departureTime.to),
            })}
          </FilterTag>
        ))}
      </>
    );
  };

  const hasFilters = useMemo(() => {
    if (isLoading || isLoadingResults) return false;
    if (!filters || isEmpty(filters)) return false;
    if (filters.priceTo || filters.priceFrom) return true;
    if (filters.airlines && filters.airlines?.length > 0) return true;
    if (filters.departureTimes && filters.departureTimes?.length > 0) return true;
    if (isDesktop && filters.stops && filters.stops[0] === '0' && filters.stops.length === 1) return false;
    if (filters.stops && filters.stops?.length > 0) return true;
    return false;
  }, [filters, isDesktop, isLoading, isLoadingResults]);

  const headerTitle = useMemo(
    () => (
      <>
        {flightStretch === FlightStretch.ONE_WAY && (
          <>
            {t('flightsList.title_plural')} {departurePoint?.code} <OneWayIcon /> {arrivalPoint?.code}
          </>
        )}
        {flightStretch === FlightStretch.ROUND_TRIP && (
          <>
            {t('flightsList.title_plural')} {departurePoint?.code} <RoundTripIcon /> {arrivalPoint?.code}
          </>
        )}
        {flightStretch === FlightStretch.MULTIDESTINATION && (
          <>
            {t('flightsList.title_plural')} {t('flightsSearch.type.multiDestination')}
          </>
        )}
      </>
    ),
    [arrivalPoint?.code, departurePoint?.code, flightStretch, t],
  );

  const headerSubtitleTooltip = () => {
    if (travelers.length > 1) {
      return (
    <Tooltip
          whiteColor={true}
          event="hover"
          content={
            getTooltipContent()
          }
        >
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <span>
            {htmlT(t, 'flightsSearch.toTraveler', {
              user: getTravelersNames(),
            })}
          </span> &nbsp; 
          <img width={20} height={20} src="/corporate/images/advice-2.svg" alt="" />
        </div>
      </Tooltip>
      );
    } else {
      return (
        <span style={{ display: 'inline'}}>
          {htmlT(t, 'flightsSearch.toTraveler', { user: getTravelersNames()})}
        </span>
      );
    }    
  };

  const getTooltipContent = () => (
    <>
      <div style={{ marginBottom: 18, fontWeight: 600 }}>
        {t('flightsSearch.additionalTraveler')}
      </div>
      {travelers.slice(1).map((t, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <img src="/corporate/images/check-copy.svg" alt="" width={14} height={14} />
          <span>
            {t.name} {t.lastName}
          </span>
        </div>
      ))}
    </>
  );

  const getTravelersNames = () => (
    `<span style='color: #F4BF31; font-weight: 600; font-size: 16px; padding-left: 4px; display: contents'>
      ${travelers[0]?.name} ${travelers[0]?.lastName} ${
        travelers.length > 1 ? '+ ' + (travelers.length - 1) : ''
      }
    </span>`
  );

  const subtitle = useMemo(() => {
    const fromDate = moment(departureDate).format('ddd[,] DD MMM');
    const toDate = returnDate ? moment(returnDate).format('ddd[,] DD MMM') : '';
    const text = returnDate ? `${fromDate} - ${toDate}` : `${fromDate}`;
    
    return travelers ? (
      <>
        {text}{' '}
        {headerSubtitleTooltip()}
      </>
    ) : (
      <>{text}</>
    );
  }, [departureDate, returnDate, t, travelers]);

  const applyOrder = async (sort: FlightResultRequestOrderCriteriaEnum) => {
    await onRequest(sort);
  };

  return (
    <>
      {!isLoading && !filterSearching && <TopButton />}
      <Mobile>
        <Header
          info={{
            flightStretch: flightStretch,
            multiDestinationTrips: multiDestinationTrips,
            departurePoint: departurePoint?.code,
            arrivalPoint: arrivalPoint?.code,
            departureDate,
            returnDate,
            isOneWay: !returnDate,
          }}
          loading={isLoading}
          goBack={() => history.push('/corporate/booking')}
          onEdit={handleEditClick}
        />
      </Mobile>

      <Desktop>
        <DesktopSearchBar isLoading={isLoading || filterSearching} />
      </Desktop>
      <HeaderSearchDesktop title={headerTitle} subtitle={subtitle} hasFilters={hasFilters} hasMap={false} />
      {!isLoading && !filterSearching && (
        <Desktop>
          <FilterTagsContainer>{renderFilterTags()}</FilterTagsContainer>
        </Desktop>
      )}
      <Container>
        <LeftContainer>
          {(isLoading || filterSearching || (flightsResult.length === 0 && hasFilters) || flightsResult.length > 0) && (
            <FlightsFilters
              show={showFilters}
              directFlights={directFlights}
              orderCriteria={orderCriteria}
              onClose={handleClickBack}
              onChange={handleFilterChange}
              availableFilters={availableFilters}
              departurePoint={departurePoint}
              arrivalPoint={arrivalPoint}
              departureDate={departureDate}
              returnDate={returnDate}
              roundTrip={roundTrip}
              baggageIncluded={baggageIncluded}
              cabin={cabin}
              isLoading={isLoading || filterSearching}
              currency={flightsResult?.first()?.price?.currencyCode ?? currency}
            />
          )}
        </LeftContainer>

        {isLoading || filterSearching ? (
          <Wrapper>
            <Body>{renderLoadingContent()}</Body>
          </Wrapper>
        ) : (
          <Wrapper>
            <Body>
              <div>
                {((isLoadingResults && flightsResult.length === 0) || flightsResult.length > 0) && (
                  <>
                    <Desktop>
                      {availableFilters?.airlines && availableFilters?.airlines[0].minPrice && (
                        <FlightsCompare
                          filters={filters}
                          airlines={availableFilters?.airlines}
                          currency={flightsResult?.first()?.price?.currencyCode ?? currency}
                          onClick={handleCompareChange}
                        />
                      )}
                    </Desktop>
                    <BestOptions
                      options={[
                        {
                          title: t('flightsList.bestOption'),
                          key: FlightResultRequestOrderCriteriaEnum.RECOMENDATION,
                          price: getTabFilterAmount(FlightResultRequestOrderCriteriaEnum.RECOMENDATION),
                          duration: getTabFilterDuration(FlightResultRequestOrderCriteriaEnum.RECOMENDATION),
                          onClick: () => applyOrder(FlightResultRequestOrderCriteriaEnum.RECOMENDATION),
                          testId: 'flight-list-filter-recommended',
                        },
                        {
                          title: t('flightsList.bestPrice'),
                          key: FlightResultRequestOrderCriteriaEnum.PRICE,
                          price: getTabFilterAmount(FlightResultRequestOrderCriteriaEnum.PRICE),
                          duration: getTabFilterDuration(FlightResultRequestOrderCriteriaEnum.PRICE),
                          onClick: () => applyOrder(FlightResultRequestOrderCriteriaEnum.PRICE),
                          testId: 'flight-list-filter-price',
                        },
                        {
                          title: t('flightsList.bestDuration'),
                          key: FlightResultRequestOrderCriteriaEnum.DURATION,
                          price: getTabFilterAmount(FlightResultRequestOrderCriteriaEnum.DURATION),
                          duration: getTabFilterDuration(FlightResultRequestOrderCriteriaEnum.DURATION),
                          onClick: () => applyOrder(FlightResultRequestOrderCriteriaEnum.DURATION),
                          testId: 'flight-list-filter-duration',
                        },
                      ]}
                      current={orderCriteria}
                      hasFilters={hasFilters}
                    />
                  </>
                )}
                <Mobile>
                  <FilterTagsContainer>{renderFilterTags()}</FilterTagsContainer>
                  {!isLoading && !filterSearching && !showFilters && !showDetails && (
                    <FloatingFilter
                      hasDirectFlights={(availableFilters?.stops || []).some((stop) => stop.text === 'Stops0')}
                      directFlights={directFlights}
                      onFilterClick={handleFilterClick}
                      onFilterDirectFlights={handleFilterDirectFlights}
                      airSearchFilterDirectFlight={directFlight}
                    />
                  )}
                </Mobile>
                {renderCards()}
              </div>
            </Body>
          </Wrapper>
        )}
      </Container>
      <FlightDetails
        show={showDetails}
        onClose={handleClickBack}
        optionId={flightOptionId}
        upSellOptionId={upSellOptionId}
        upSellId={upSellOptionId && upSellId ? upSellId : ''}
        travelers={travelers}
        index={selectedIndex}
      />

      {showUpSell && selectedFlight && (
        <UpSellOptions
          onClose={() => history.goBack()}
          onSelect={handleUpSellSelect}
          onChange={handleUpSellChange}
          show={showUpSell}
          selectedFlight={
            flightOptionId
              ? {
                  optionId: flightOptionId,
                  provider: selectedFlight?.provider,
                  upSellEnabled: selectedFlight?.upSellEnabled,
                  sectors: selectedFlight.sectors,
                }
              : undefined
          }
          departureDate={departureDate}
          departurePoint={departurePoint}
          arrivalPoint={arrivalPoint}
          distanceRestriction={distanceRestriction}
          returnDate={returnDate}
          cabin={cabin}
          roundTrip={roundTrip}
          baggageIncluded={baggageIncluded}
        />
      )}

      <Mobile>
        <FlightsSearchEdit show={showEdit} onClose={handleClickBack} />
      </Mobile>
    </>
  );
};

export default FlightsList;
